import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  QrCodeIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { generalApi } from '../../api/generalApi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import Loading from '../../components/ui/Loading';
import { XMarkIcon } from '@heroicons/react/24/outline';

const QR_BASE_URL = typeof window !== 'undefined' && window.ENV?.QR_BASE_URL
  ? window.ENV.QR_BASE_URL
  : 'https://qr.akkahotels.com';

/** QR linkleri için production API (localhost asla kullanılmaz). */
const getPublicApiBase = () => {
  if (typeof window !== 'undefined' && window.ENV?.API_URL) {
    return window.ENV.API_URL.replace(/\/$/, '');
  }
  return 'https://app.akkahotels.com/api';
};

const getPublicPdfUrl = (slug) =>
  `${getPublicApiBase()}/general/custom-pages/resolve/${encodeURIComponent(slug)}/pdf/`;

const pageHasPdf = (page) => Boolean(page?.pdf_url || page?.pdf_file);

const slugify = (str) =>
  String(str || '')
    .toLowerCase()
    .replace(/[ıİ]/g, 'i')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const CustomPagesPage = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({ slug: '', title: '', content: '' });
  const [pdfFile, setPdfFile] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedPageForQr, setSelectedPageForQr] = useState(null);

  const loadPages = async () => {
    try {
      const res = await generalApi.getCustomPages();
      const data = Array.isArray(res) ? res : (res?.data || res?.results || []);
      setPages(data);
    } catch (e) {
      console.error(e);
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPages();
  }, []);

  const openCreate = () => {
    setEditingPage(null);
    setFormData({ slug: '', title: '', content: '' });
    setPdfFile(null);
    setRemovePdf(false);
    setError('');
    setModalOpen(true);
  };

  const openEdit = (page) => {
    setEditingPage(page);
    setFormData({
      slug: page.slug || '',
      title: page.title || '',
      content: page.content || '',
    });
    setPdfFile(null);
    setRemovePdf(false);
    setError('');
    setModalOpen(true);
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData((prev) => ({
      ...prev,
      title,
      slug: editingPage ? prev.slug : slugify(title),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.slug?.trim()) {
      setError('Slug zorunludur.');
      return;
    }
    if (!formData.title?.trim()) {
      setError('Başlık zorunludur.');
      return;
    }
    setSaveLoading(true);
    try {
      const slug = formData.slug.trim();
      const title = formData.title.trim();
      const content = formData.content?.trim() || '';

      if (editingPage) {
        if (pdfFile) {
          const fd = new FormData();
          fd.append('slug', slug);
          fd.append('title', title);
          fd.append('content', content);
          fd.append('pdf_file', pdfFile, pdfFile.name);
          await generalApi.updateCustomPage(editingPage.slug, fd);
        } else {
          const payload = { slug, title, content };
          if (removePdf) {
            payload.clear_pdf = true;
          }
          await generalApi.updateCustomPage(editingPage.slug, payload);
        }
      } else if (pdfFile) {
        const fd = new FormData();
        fd.append('slug', slug);
        fd.append('title', title);
        fd.append('content', content);
        fd.append('pdf_file', pdfFile, pdfFile.name);
        await generalApi.createCustomPage(fd);
      } else {
        await generalApi.createCustomPage({ slug, title, content });
      }
      setModalOpen(false);
      loadPages();
    } catch (err) {
      setError(err.message || 'Kayıt yapılamadı.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async (page) => {
    if (!window.confirm(`"${page.title}" sayfasını silmek istediğinize emin misiniz?`)) return;
    try {
      await generalApi.deleteCustomPage(page.slug);
      loadPages();
    } catch (err) {
      alert(err.message || 'Silme başarısız.');
    }
  };

  const openQrModal = (page) => {
    setSelectedPageForQr(page);
    setQrModalOpen(true);
  };

  const getPageUrl = (slug) => `${QR_BASE_URL.replace(/\/$/, '')}/${slug}`;

  /** PDF varsa QR doğrudan menü dosyasına gider (en hızlı yol). */
  const getQrTargetUrl = (page) => {
    if (pageHasPdf(page)) return getPublicPdfUrl(page.slug);
    return getPageUrl(page.slug);
  };

  const handleCopyLink = async () => {
    if (!selectedPageForQr?.slug) return;
    try {
      await navigator.clipboard.writeText(getQrTargetUrl(selectedPageForQr));
      alert('Link kopyalandı.');
    } catch {
      alert('Kopyalama başarısız.');
    }
  };

  const handleDownloadQr = () => {
    if (!selectedPageForQr?.slug) return;
    const url = getQrTargetUrl(selectedPageForQr);
    const qrApi = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
    const link = document.createElement('a');
    link.href = qrApi;
    link.download = `qr-${selectedPageForQr.slug}.png`;
    link.target = '_blank';
    link.click();
  };

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <QrCodeIcon className="w-8 h-8 text-blue-600" />
              QR Özel Sayfalar
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Özel sayfa oluşturun. QR taratıldığında qr.akkahotels.com/slug ile bu sayfa açılır.
            </p>
          </div>
          <Button onClick={openCreate} className="inline-flex items-center gap-2">
            <PlusIcon className="w-5 h-5" />
            Yeni Sayfa
          </Button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {pages.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              Henüz özel sayfa yok. Yeni sayfa ekleyin.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {pages.map((page) => (
                <li
                  key={page.id || page.slug}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{page.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {getPageUrl(page.slug)}
                      {page.pdf_url ? (
                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                          PDF
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => openQrModal(page)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                      <QrCodeIcon className="w-4 h-4" />
                      QR
                    </button>
                    <button
                      onClick={() => openEdit(page)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                      Düzenle
                    </button>
                    <button
                      onClick={() => handleDelete(page)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <TrashIcon className="w-4 h-4" />
                      Sil
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ResponsiveModal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {editingPage ? 'Sayfayı Düzenle' : 'Yeni Özel Sayfa'}
          </h3>
          <button
            type="button"
            onClick={() => setModalOpen(false)}
            className="p-1 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Kapat"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label htmlFor="title">Başlık</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={handleTitleChange}
              placeholder="Örn: Kampanya Sayfası"
              required
            />
          </div>
          <div>
            <Label htmlFor="slug">Slug (URL)</Label>
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) => setFormData((p) => ({ ...p, slug: e.target.value }))}
              placeholder="ornek-sayfa"
              disabled={!!editingPage}
              className={editingPage ? 'bg-gray-100 dark:bg-gray-700' : ''}
            />
            {!editingPage && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                qr.akkahotels.com/{formData.slug || 'slug'} adresinde açılır. Oluşturulduktan sonra değiştirilemez.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="content">İçerik (HTML)</Label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData((p) => ({ ...p, content: e.target.value }))}
              placeholder="<p>İçerik buraya...</p>"
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <Label htmlFor="custom-page-pdf">PDF (isteğe bağlı)</Label>
            <p className="mb-1.5 text-xs text-gray-500 dark:text-gray-400">
              QR sayfasında HTML ile birlikte gösterilir; indirilebilir.
            </p>
            <input
              id="custom-page-pdf"
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && f.type && f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
                  setError('Lütfen sadece PDF yükleyin.');
                  e.target.value = '';
                  return;
                }
                setError('');
                setPdfFile(f || null);
                if (f) {
                  setRemovePdf(false);
                }
              }}
              className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-800 dark:file:text-gray-200"
            />
            {editingPage?.pdf_url && !pdfFile && !removePdf && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Bu sayfada PDF tanımlı.</p>
            )}
            {editingPage?.pdf_url && !pdfFile && (
              <label className="mt-2 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 dark:border-gray-600"
                  checked={removePdf}
                  onChange={(e) => setRemovePdf(e.target.checked)}
                />
                Mevcut PDF dosyasını kaldır
              </label>
            )}
          </div>
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>
              İptal
            </Button>
            <Button type="submit" disabled={saveLoading}>
              {saveLoading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </form>
      </ResponsiveModal>

      <ResponsiveModal isOpen={qrModalOpen} onClose={() => setQrModalOpen(false)} className="max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">QR Kod</h3>
          <button
            type="button"
            onClick={() => setQrModalOpen(false)}
            className="p-1 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Kapat"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {selectedPageForQr && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {pageHasPdf(selectedPageForQr) ? (
                  <>
                    Bu sayfada PDF menü var. QR taratıldığında menü <strong>doğrudan</strong> açılır.
                  </>
                ) : (
                  <>
                    Taratıldığında <strong>{getPageUrl(selectedPageForQr.slug)}</strong> açılır.
                  </>
                )}
              </p>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-shrink-0">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(getQrTargetUrl(selectedPageForQr))}`}
                    alt="QR Code"
                    className="w-48 h-48 rounded-lg border border-gray-200 dark:border-gray-600"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Link</p>
                  <p className="text-sm text-gray-900 dark:text-gray-100 break-all">{getQrTargetUrl(selectedPageForQr)}</p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleDownloadQr}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" />
                      İndir
                    </button>
                    <button
                      onClick={handleCopyLink}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      Linki Kopyala
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default CustomPagesPage;
